"use client";

import { Checkbox } from "@/components/ui/Checkbox";
import { PATHS } from "@/consts";
import env from "@/env.mjs";
import { useBoolean } from "@/hooks/useBoolean";
import { useRichText } from "@/hooks/useRichText";
import { useTranslations } from "@/i18n/client";
import { Link } from "@/i18n/routing";
import { useMutation } from "@tanstack/react-query";
import { ofetch } from "ofetch";
import { ChangeEvent } from "react";
import styles from "./index.module.css";

const base = env.NEXT_PUBLIC_CENTRA_CHECKOUT;

interface IAccountNewsletter {
  newsletterTitle?: string;
  newsletterText?: string;
  newsletterTerms?: string;
  isUserSignedUp?: boolean;
  email?: string;
}

const AccountNewsletter = ({
  newsletterTitle,
  newsletterText,
  isUserSignedUp,
  email,
}: IAccountNewsletter) => {
  const mutation = useMutation({
    mutationFn: async (email: string) => {
      const res = await ofetch(`${base}/newsletter-subscription/${email}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email_field: email,
        }),
      });
      return res;
    },
  });

  const accountCheckboxState = useBoolean(isUserSignedUp);
  const t = useTranslations();
  const label = useLabel(mutation.isSuccess, !!isUserSignedUp);

  /* Function for later when centra implements unsubscribe API */
  const toggleAccountCheckboxState = (e: ChangeEvent<HTMLInputElement>) => {
    accountCheckboxState.toggle();
    handleChecked(e);
  };

  const handleChecked = async (_e: ChangeEvent<HTMLInputElement>) => {
    email && mutation.mutate(email);
  };

  return (
    <div>
      <div className={styles.newsletter__inner}>
        {newsletterTitle && (
          <h2 className={styles.newsletterTitle}>{newsletterTitle}</h2>
        )}
        {newsletterText && (
          <p className={styles.newsletterText}>{newsletterText}</p>
        )}
        <form className={styles.checkboxForm}>
          <Checkbox
            checked={accountCheckboxState.value}
            disabled={accountCheckboxState.value}
            onChange={toggleAccountCheckboxState}
            label={label}
          />
        </form>
      </div>

      {mutation.isError && (
        <p className={styles.message}>{t("footer.newsletter.error")}</p>
      )}
    </div>
  );
};

export default AccountNewsletter;

const useLabel = (isSuccess: boolean, isUserSignedUp: boolean) => {
  const t = useTranslations();
  const { rich } = useRichText();
  if (isSuccess) {
    return t("footer.newsletter.success");
  }
  if (isUserSignedUp) {
    return t("footer.newsletter.signedUp");
  }

  return rich(t("checkout.newsletter"), {
    link: chunks => (
      <Link key={1} href={PATHS.privacyPolicy}>
        {chunks}
      </Link>
    ),
  });
};
